// Copyright ©️ 2026 eVolve MEP, LLC

import { Anchor, type AnchorProps, Flex } from '@mantine/core';

import type { ShopNavigationRoute } from 'app/ModuleRoutes';
import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { getHistory, useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { isNotNil } from 'helpers/isNotNil';

import type { Page, PageTitle } from './types';
import { useEvolveLocation } from './useEvolveLocation';

const Breadcrumb = ({
  title,
  index,
  url,
  clearHistory,
}: {
  title: string;
  clearHistory?: boolean;
} & (
  | {
      index: number;
      url?: never;
    }
  | {
      url: ShopNavigationRoute;
      index?: never;
    }
)) => {
  const navigate = useEvolveNavigate(title, clearHistory, true);

  const doOnClick = () => {
    if (isNotNil(url)) {
      void navigate(url);
    } else {
      void navigate(-(index ?? 1));
    }
  };
  return (
    <>
      <Anchor
        c="dimmed"
        lh={1.14}
        onClick={doOnClick}
        lineClamp={1}
        style={{ wordWrap: 'unset', maxWidth: 300 }}
        title={title}
      >
        {title}
      </Anchor>
      <EvolveIcon icon="ArrowRight" size={8} color="dimmed" />
    </>
  );
};

const headerProps: AnchorProps = {
  size: '26px',
  fw: 600,
  mr: 'md',
  style: {
    cursor: 'initial',
  },
} as const;

export type PageBreadcrumbProps = {
  /** If `true`, will ignore history when rendering breadcrumbs */
  noHistory?: boolean;
} & (
  | {
      /** Title of the page we are on */
      title: PageTitle;
      /** The parent page to fall back to if we are unable to pull it from react-router */
      parentPage?: Page;
    }
  | {
      // If `parentPage` is provided, `title` can be a raw string.
      title: PageTitle | string;
      parentPage: Page;
    }
);

export const PageBreadcrumb = ({ title, parentPage, noHistory = false, ...props }: PageBreadcrumbProps) => {
  const { state: locationState } = useEvolveLocation();
  const history = getHistory(locationState);
  const hasLocationHistory = !noHistory && history.length > 0;
  const hasParentPage = hasLocationHistory || isNotNil(parentPage);

  return (
    <Flex align="center" gap="xs" wrap="wrap">
      {hasLocationHistory &&
        history.map(({ title: breadcrumbTitle }, i) => (
          <Breadcrumb key={breadcrumbTitle} title={breadcrumbTitle} index={history.length - i} {...props} />
        ))}
      {!hasLocationHistory && isNotNil(parentPage) && <Breadcrumb clearHistory {...parentPage} />}
      <Anchor
        lineClamp={1}
        c="dark"
        lh={1.14}
        fw={500}
        maw={450}
        style={{ cursor: 'initial', wordBreak: 'keep-all' }}
        underline="never"
        title={title}
        {...(!hasParentPage ? headerProps : {})}
      >
        {title}
      </Anchor>
    </Flex>
  );
};
